import { MoreVertical, Search, Trash2 } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Menu } from "src/components/Default/LekUI/Menu";
import { TextFieldGrey } from "src/components/Default/LekUI/TextField";
import { ConversationItem } from "src/components/GlobalChatbot/ConversationItem";
import { groupConversationsByDate } from "src/components/GlobalChatbot/groupConversationsByDate";
import {
	useChatbotConversations,
	useDeleteChatbotConversation,
} from "src/hooks/useChatbotConversations";

interface ConversationListProps {
	activeConversationId: string | null;
	onSelect: (conversationId: string) => void;
	onDeleted: (conversationId: string) => void;
}

export const ConversationList = ({
	activeConversationId,
	onSelect,
	onDeleted,
}: ConversationListProps) => {
	const { t } = useTranslation();
	const { data: conversations = [] } = useChatbotConversations();
	const { mutate: deleteConversation } = useDeleteChatbotConversation();
	const [query, setQuery] = useState("");

	const filtered = conversations.filter((conversation) =>
		conversation.question.toLowerCase().includes(query.trim().toLowerCase()),
	);
	const groups = groupConversationsByDate(filtered, t);

	return (
		<div className="chatbot-history">
			<div className="chatbot-history-search">
				<TextFieldGrey
					data-test-id="global-chatbot-history-search"
					placeholder={t("globalChatbot.history.searchPlaceholder", "Szukaj…")}
					fullWidth
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					onClear={() => setQuery("")}
					slotProps={{
						input: {
							startAdornment: (
								<Search className="icon-small icon-black-80 mr-8" />
							),
						},
					}}
				/>
			</div>

			<div className="chatbot-history-list scroll">
				{activeConversationId === null && !query && (
					<div
						data-test-id="global-chatbot-history-draft-item"
						className="chatbot-history-item item-selected"
					>
						<div className="chatbot-history-item-top">
							<span className="chatbot-history-item-question">
								{t("globalChatbot.newConversation", "Nowa konwersacja")}
							</span>
						</div>
					</div>
				)}

				{groups.map((group) => (
					<div key={group.label}>
						<div className="chatbot-history-group-label">{group.label}</div>
						<div className="flex flex-col gap-8">
							{group.items.map((conversation) => (
								<ConversationItem
									key={conversation.id}
									conversation={conversation}
									selected={conversation.id === activeConversationId}
									onSelect={onSelect}
									actions={
										<Menu
											className="chatbot-history-item-menu"
											buttonTestId="global-chatbot-history-item-menu"
											icon={<MoreVertical className="icon-super-small" />}
											items={[
												{
													title: t(
														"globalChatbot.history.delete",
														"Usuń konwersację",
													),
													Icon: <Trash2 className="icon-small" />,
													className: "chatbot-menu-item--danger",
													onClick: (event?: React.MouseEvent) => {
														event?.stopPropagation();
														deleteConversation(conversation.id, {
															onSuccess: () => onDeleted(conversation.id),
														});
													},
													testId: "global-chatbot-history-item-delete",
												},
											]}
										/>
									}
								/>
							))}
						</div>
					</div>
				))}

				{groups.length === 0 && query && (
					<div className="text-center text-grey size-12-light p-24">
						{t(
							"globalChatbot.history.noResults",
							'Brak wyników dla "{{query}}"',
							{
								query,
							},
						)}
					</div>
				)}
			</div>
		</div>
	);
};
